import type { InteriorWoodSpecies } from "../finishes/types";
/**
 * Interior object model — lavlayout och aggregatplacering som data.
 *
 * The engines used to own the interior completely: bench positions were
 * hard-coded vectors in `squareSauna.ts` / `twoRoomSauna.ts` and the BOM
 * bought bench timber from a width heuristic that never looked at them.
 * This module makes the layout a first-class, user-editable object that
 * both the BOM and (from issue #131) the renderer derive from.
 *
 * Frame of reference
 * ------------------
 * A room is an axis-aligned rectangle **in millimetres, in the engine's
 * model frame** (X across the building width, Z along its length, origin
 * at the building centre). Bench runs are stored *wall-relative* — which
 * wall, how far along it, how long, how deep — because that is both the
 * vocabulary a builder uses and the representation that survives a
 * dimension change without re-solving world coordinates.
 *
 * Pure geometry + numbers. No THREE, no strings: user-facing text is
 * emitted as translation keys (see the domain i18n key note).
 */

/**
 * Wall identifiers, matching the engines' opening walls.
 * `back` = −Z, `front` = +Z, `left` = −X, `right` = +X.
 */
export type InteriorWall = "front" | "back" | "left" | "right";

/**
 * Which lav level a run belongs to. Drives the seat height that the
 * ergonomics domain (Löylyns lag) hands out, not the geometry here.
 */
export type BenchTier = "upper" | "lower";

/** Two-room saunas have two rooms; a fyrkantig has only the hot room. */
export type InteriorRoomId = "sauna" | "relax";

/** Inner (finished) bounds of one room, millimetres, model frame. */
export interface InteriorRoom {
	id: InteriorRoomId;
	minXMm: number;
	maxXMm: number;
	minZMm: number;
	maxZMm: number;
	/** Walls with openings: do not assume a usable end bearing without a detailed fixing plan. */
	benchSupportExcludedWalls?: InteriorWall[];
	/**
	 * Walls that are glass floor to ceiling (the panorama wall). A lav end
	 * against one is in full view from outside, so it is dressed like an
	 * end standing free in the room.
	 */
	glazedWalls?: InteriorWall[];
	/**
	 * The design's wall height, mm (issue 155). The lavar hang from the wall
	 * top, so their legs grow with it. Absent = the rule default.
	 */
	wallHeightMm?: number;
}

export type BenchSupportMode = "floor" | "wall" | "wall+frontLegs" | "endWalls";

export interface BenchRun {
	/** Stable id — survives edits so undo/redo and selection can track a run. */
	id: string;
	room: InteriorRoomId;
	wall: InteriorWall;
	tier: BenchTier;
	/** Start of the run measured along the wall from its low-coordinate corner. */
	startMm: number;
	/** Run length along the wall. */
	lengthMm: number;
	/** Seat depth, measured out from the wall. */
	depthMm: number;
	/**
	 * Gap between the wall face and this run's wall-side edge. `0` permits a
	 * back-wall ledger; the classic foot lav sits at the upper lav's
	 * depth so the two tiers stack front-to-back rather than overlapping.
	 */
	offsetFromWallMm: number;
	/**
	 * Height of the posts under the seat, when the tier's own default is wrong.
	 *
	 * The upper lav is wall-hung only when adjacent to its wall; detached runs
	 * have posts reaching the floor unless supported at both ends. A relax bench sits at ordinary seat height on
	 * ~432 mm legs. Omitted for everything the tier already describes.
	 */
	legHeightMm?: number;
	/** Authored seat TOP above the finished floor; absent follows the tier. */
	seatHeightMm?: number;
	/** Absent uses contact-based auto support. Explicit choices must fit the room. */
	supportMode?: BenchSupportMode;
	/** Absent follows the room finish. */
	woodSpecies?: InteriorWoodSpecies;
	/** Seat-board gap; absent retains the legacy 18 mm layout. */
	slatGapMm?: number;
	/** Same-room modules move vertically together, preserving their height gaps. */
	heightGroupId?: string;
	/** Absent preserves the generated front; false leaves the frame open. */
	skirtEnabled?: boolean;
	/** Wall-mounted horizontal 28×90 boards; absent means no backrest. */
	backrestEnabled?: boolean;
	backrestStartMm?: number;
	backrestLengthMm?: number;
	/** Gap from the seat top to the first backrest board. */
	backrestAboveSeatMm?: number;
	backrestBoardCount?: number;
}

export interface HeaterPlacement {
	wall: InteriorWall;
	/** Heater centre measured along the wall from its low-coordinate corner. */
	offsetMm: number;
	/** Gap between the wall face and the heater's back face. */
	wallGapMm: number;
}

export type InteriorLayoutVariantId =
	| "straight"
	| "two-tier"
	| "l-shape"
	| "u-shape";

export interface InteriorLayout {
	/**
	 * The variant this layout STARTED as. Variants are starting points, not
	 * locked presets — a user is free to drag a run and keep the label.
	 */
	variant: InteriorLayoutVariantId;
	benchRuns: BenchRun[];
	heater: HeaterPlacement;
}

/** Axis-aligned footprint in millimetres, model frame. */
export interface FootprintMm {
	minXMm: number;
	maxXMm: number;
	minZMm: number;
	maxZMm: number;
}

/**
 * The heater's footprint in the MODEL frame: X across, Z along the building.
 *
 * Wall-independent on purpose. The engines never rotate the unit, so which of
 * the two measurements runs along a wall depends entirely on which wall it
 * stands against — see `heaterExtentsFor`. Storing the already-oriented pair
 * meant that moving an authored heater from a long wall to a short one kept the
 * long-wall orientation, and every consumer measured a heater with its width
 * and depth swapped.
 */
export interface HeaterSizeMm {
	widthMm: number;
	depthMm: number;
}

/**
 * The same footprint resolved against one wall: one measurement along it, one
 * out from it.
 */
export interface HeaterExtentsMm {
	alongWallMm: number;
	fromWallMm: number;
}

/** Resolve a heater's model-frame size against the wall it stands on. */
export function heaterExtentsFor(
	size: HeaterSizeMm,
	wall: InteriorWall,
): HeaterExtentsMm {
	return wallRunsAlongX(wall)
		? { alongWallMm: size.widthMm, fromWallMm: size.depthMm }
		: { alongWallMm: size.depthMm, fromWallMm: size.widthMm };
}

// ─── Room helpers ───────────────────────────────────────────

export function roomWidthMm(room: InteriorRoom): number {
	return room.maxXMm - room.minXMm;
}

export function roomLengthMm(room: InteriorRoom): number {
	return room.maxZMm - room.minZMm;
}

export function roomFloorAreaSqm(room: InteriorRoom): number {
	return (roomWidthMm(room) * roomLengthMm(room)) / 1_000_000;
}

/** True when the wall runs along X (back/front), false when it runs along Z. */
export function wallRunsAlongX(wall: InteriorWall): boolean {
	return wall === "back" || wall === "front";
}

/** Full length of the wall a run is measured along. */
export function wallAxisLengthMm(
	room: InteriorRoom,
	wall: InteriorWall,
): number {
	return wallRunsAlongX(wall) ? roomWidthMm(room) : roomLengthMm(room);
}

/** Model-frame coordinate of the wall's low-coordinate corner. */
export function wallAxisOriginMm(
	room: InteriorRoom,
	wall: InteriorWall,
): number {
	return wallRunsAlongX(wall) ? room.minXMm : room.minZMm;
}

/**
 * Signed direction pointing from the wall face into the room, along the
 * wall's normal axis. `back`/`left` face the positive side, `front`/`right`
 * the negative one.
 */
export function wallInwardSign(wall: InteriorWall): 1 | -1 {
	return wall === "back" || wall === "left" ? 1 : -1;
}

/** Model-frame coordinate of the wall face, on the wall's normal axis. */
export function wallFaceMm(room: InteriorRoom, wall: InteriorWall): number {
	switch (wall) {
		case "back":
			return room.minZMm;
		case "front":
			return room.maxZMm;
		case "left":
			return room.minXMm;
		case "right":
			return room.maxXMm;
	}
}

function spanFromWall(
	room: InteriorRoom,
	wall: InteriorWall,
	offsetMm: number,
	depthMm: number,
): { min: number; max: number } {
	const face = wallFaceMm(room, wall);
	const sign = wallInwardSign(wall);
	const near = face + sign * offsetMm;
	const far = near + sign * depthMm;
	return { min: Math.min(near, far), max: Math.max(near, far) };
}

/**
 * Assemble a footprint from a span along a wall and a span out from it.
 *
 * Which of the two lands on X and which on Z depends only on the wall's axis —
 * the one place that swap should be written down. Anything measuring a
 * wall-mounted thing (bench run, heater, door swing) goes through here.
 */
export function footprintFromWallSpans(
	wall: InteriorWall,
	along: { min: number; max: number },
	across: { min: number; max: number },
): FootprintMm {
	return wallRunsAlongX(wall)
		? {
				minXMm: along.min,
				maxXMm: along.max,
				minZMm: across.min,
				maxZMm: across.max,
			}
		: {
				minXMm: across.min,
				maxXMm: across.max,
				minZMm: along.min,
				maxZMm: along.max,
			};
}

/** Model-frame footprint of a bench run. */
export function benchRunFootprint(
	run: BenchRun,
	room: InteriorRoom,
): FootprintMm {
	const origin = wallAxisOriginMm(room, run.wall);
	return footprintFromWallSpans(
		run.wall,
		{ min: origin + run.startMm, max: origin + run.startMm + run.lengthMm },
		spanFromWall(room, run.wall, run.offsetFromWallMm, run.depthMm),
	);
}

/** Model-frame footprint of the heater at its placement. */
export function heaterFootprint(
	placement: HeaterPlacement,
	room: InteriorRoom,
	size: HeaterSizeMm,
): FootprintMm {
	// Orient against the placement's OWN wall, not the one it was measured on.
	const extents = heaterExtentsFor(size, placement.wall);
	const center = wallAxisOriginMm(room, placement.wall) + placement.offsetMm;
	return footprintFromWallSpans(
		placement.wall,
		{
			min: center - extents.alongWallMm / 2,
			max: center + extents.alongWallMm / 2,
		},
		spanFromWall(room, placement.wall, placement.wallGapMm, extents.fromWallMm),
	);
}

/** Face-to-face gap between two footprints (negative when they overlap). */
export function footprintGapMm(a: FootprintMm, b: FootprintMm): number {
	const gapX = Math.max(a.minXMm - b.maxXMm, b.minXMm - a.maxXMm);
	const gapZ = Math.max(a.minZMm - b.maxZMm, b.minZMm - a.maxZMm);
	// Separated on either axis → that axis' gap is ≥ 0 and wins. Overlapping on
	// both → every candidate is negative and the largest is the shallower
	// penetration, so a nicked corner reads as a small violation rather than as
	// the full depth of the deeper overlap.
	return Math.max(gapX, gapZ);
}

export function footprintsOverlap(a: FootprintMm, b: FootprintMm): boolean {
	return (
		a.minXMm < b.maxXMm &&
		a.maxXMm > b.minXMm &&
		a.minZMm < b.maxZMm &&
		a.maxZMm > b.minZMm
	);
}

export function footprintAreaSqm(box: FootprintMm): number {
	return (
		(Math.max(0, box.maxXMm - box.minXMm) *
			Math.max(0, box.maxZMm - box.minZMm)) /
		1_000_000
	);
}
